import { cva } from 'class-variance-authority';
import { cn } from '@/libs/utils/cn';
import { ToggleGroup, ToggleGroupItem } from '../atoms/toggle-group';
import { Tooltip, TooltipContent, TooltipTrigger } from '../atoms/tooltip';

const toggleItemVariants = cva(
  'relative z-10 text-xs rounded-sm px-2.5 py-1.5 font-normal text-gray-600 bg-white/40 transition-all duration-150 ease-out hover:bg-white/60 data-[state=on]:text-black data-[state=on]:bg-transparent flex-1 !shrink',
  {
    variants: {
      disabled: {
        true: 'opacity-50 cursor-not-allowed hover:bg-white/40',
        false: '',
      },
    },
    defaultVariants: {
      disabled: false,
    },
  },
);

interface MetricToggleProps {
  className?: string;
  value: 'forecast' | 'keyMetrics';
  onValueChange: (value: 'forecast' | 'keyMetrics') => void;
  options?: string[];
  forecastDisabled?: boolean;
}

export function MetricToggle(props: MetricToggleProps) {
  const {
    value,
    className,
    onValueChange,
    options = ['Key metrics', 'Demand forecast'],
    forecastDisabled = false,
  } = props;
  return (
    <ToggleGroup
      type="single"
      value={value}
      onValueChange={onValueChange}
      className={cn('relative flex w-full p-1 gap-2 bg-slate-50 rounded-sm', className)}
    >
      <div
        className={cn(
          'absolute top-1 left-1 h-[calc(100%-8px)] rounded-sm bg-white border border-neutral-100 shadow-sm transition-all duration-200 ease-out',
          value === 'forecast'
            ? 'w-[calc(50%-6px)] translate-x-[calc(100%+4px)]'
            : 'w-[calc(50%-6px)]',
        )}
      />
      <ToggleGroupItem value="keyMetrics" aria-label={options[0]} className={toggleItemVariants()}>
        {options[0]}
      </ToggleGroupItem>
      {forecastDisabled ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="flex-1">
              <ToggleGroupItem
                value="forecast"
                aria-label={options[1]}
                disabled
                className={cn(toggleItemVariants({ disabled: true }), 'w-full')}
              >
                {options[1]}
              </ToggleGroupItem>
            </span>
          </TooltipTrigger>
          <TooltipContent>
            <p>Forecasted data not available for this location</p>
          </TooltipContent>
        </Tooltip>
      ) : (
        <ToggleGroupItem value="forecast" aria-label={options[1]} className={toggleItemVariants()}>
          {options[1]}
        </ToggleGroupItem>
      )}
    </ToggleGroup>
  );
}
